import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { Button } from '@/Components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/Components/ui/card';
import { Progress } from '@/Components/ui/progress';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/Components/ui/dialog';

enum ProvisioningStatus {
  Pending = 0,
  CreatingDatabase = 1,
  ApplyingMigrations = 2,
  CreatingCompany = 3,
  CreatingUser = 4,
  Finalizing = 5,
  Ready = 6,
  Failed = 7
}

const statusMessages = {
  [ProvisioningStatus.Pending]: 'Preparing your workspace...',
  [ProvisioningStatus.CreatingDatabase]: 'Creating your database...',
  [ProvisioningStatus.ApplyingMigrations]: 'Setting up database structure...',
  [ProvisioningStatus.CreatingCompany]: 'Creating your company profile...',
  [ProvisioningStatus.CreatingUser]: 'Setting up your admin account...',
  [ProvisioningStatus.Finalizing]: 'Finalizing setup...',
  [ProvisioningStatus.Ready]: 'Your workspace is ready!',
  [ProvisioningStatus.Failed]: 'Setup failed'
};

const statusProgress = {
  [ProvisioningStatus.Pending]: 5,
  [ProvisioningStatus.CreatingDatabase]: 20,
  [ProvisioningStatus.ApplyingMigrations]: 40,
  [ProvisioningStatus.CreatingCompany]: 60,
  [ProvisioningStatus.CreatingUser]: 80,
  [ProvisioningStatus.Finalizing]: 95,
  [ProvisioningStatus.Ready]: 100,
  [ProvisioningStatus.Failed]: 0
};

export default function WelcomePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tenantId = searchParams.get('tenantId');
  const companyName = searchParams.get('companyName');

  const [status, setStatus] = useState<ProvisioningStatus>(ProvisioningStatus.Pending);
  const [error, setError] = useState<string | null>(null);
  const [showReadyDialog, setShowReadyDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const pollStatus = useCallback(async () => {
    if (!tenantId) return;

    try {
      const response = await fetch(`/Account/provisioning-status/${tenantId}`);
      if (response.ok) {
        const data = await response.json();
        const statusEnum = ProvisioningStatus[data.status as keyof typeof ProvisioningStatus];
        setStatus(statusEnum);
        
        if (statusEnum === ProvisioningStatus.Ready && !showReadyDialog) {
          setShowReadyDialog(true);
        }
        
        if (data.error) {
          setError(data.error);
        }
      }
    } catch (err) {
      console.error('Error polling status:', err);
    }
  }, [tenantId, showReadyDialog]);

  useEffect(() => {
    if (!tenantId) {
      navigate('/login');
      return;
    }

    // Set up SignalR connection
    const connection = new HubConnectionBuilder()
      .withUrl('/hubs/provisioning')
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    connection.on('ProvisioningStatusUpdate', (data: { tenantId: string; status: string; error?: string }) => {
      console.log('Status update:', data);
      const statusEnum = ProvisioningStatus[data.status as keyof typeof ProvisioningStatus];
      setStatus(statusEnum);
      if (data.error) {
        setError(data.error);
      }
    });

    connection.on('ProvisioningComplete', (data: { tenantId: string; message: string }) => {
      console.log('Provisioning complete:', data);
      setStatus(ProvisioningStatus.Ready);
      setShowReadyDialog(true);
    });

    connection.on('ProvisioningFailed', (data: { tenantId: string; error: string }) => {
      console.log('Provisioning failed:', data);
      setStatus(ProvisioningStatus.Failed);
      setError(data.error);
      setShowErrorDialog(true);
    });

    // Start connection and join tenant group
    connection.start()
      .then(() => {
        console.log('SignalR connected');
        return connection.invoke('JoinTenantGroup', tenantId);
      })
      .then(() => {
        console.log('Joined tenant group:', tenantId);
        // Also poll for initial status
        return pollStatus();
      })
      .catch(err => console.error('SignalR connection error:', err));

    // Polling backup (in case SignalR fails)
    const pollInterval = setInterval(pollStatus, 5000);

    return () => {
      clearInterval(pollInterval);
      connection.stop();
    };
  }, [tenantId, navigate, pollStatus]);

  const handleGoToDashboard = () => {
    // Navigate to login page with email pre-filled
    const email = searchParams.get('email');
    if (email) {
      navigate(`/login?email=${encodeURIComponent(email)}&message=${encodeURIComponent('Your account is ready! Please log in to continue.')}`);
    } else {
      navigate('/login');
    }
  };

  const handleRetry = () => {
    window.location.href = '/register';
  };

  return (
    <div className="flex flex-row min-h-screen justify-center items-center bg-gradient-to-br from-background to-secondary/20">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold">
            {status === ProvisioningStatus.Ready ? '🎉 Welcome!' : '⏳ Setting Up Your Workspace'}
          </CardTitle>
          <CardDescription>
            {companyName && `${companyName} - `}
            {statusMessages[status]}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {status !== ProvisioningStatus.Failed && (
            <>
              <div className="space-y-2">
                <Progress value={statusProgress[status]} className="h-2" />
                <p className="text-sm text-muted-foreground text-center">
                  {Math.round(statusProgress[status])}% Complete
                </p>
              </div>

              {status !== ProvisioningStatus.Ready && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>This usually takes 1-2 minutes...</span>
                </div>
              )}
            </>
          )}

          {status === ProvisioningStatus.Failed && error && (
            <div className="flex items-center gap-2 p-4 border border-destructive bg-destructive/10 rounded-md text-destructive">
              <AlertCircle className="h-5 w-5" />
              <div>
                <p className="font-semibold">Setup Failed</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          <div className="bg-muted p-6 rounded-lg space-y-4">
            <h3 className="font-semibold text-lg">📚 Getting Started</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>✅ Create your first project</li>
              <li>✅ Invite team members</li>
              <li>✅ Set up project workflows</li>
              <li>✅ Track progress with RAIDs</li>
            </ul>
          </div>

          {/* Embed optional intro video */}
          <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
            <p className="text-muted-foreground">Optional: Intro video placeholder</p>
          </div>

          <div className="flex gap-3">
            {status === ProvisioningStatus.Failed && (
              <Button onClick={handleRetry} variant="outline" className="flex-1" size="lg">
                Try Again
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={showReadyDialog} onOpenChange={setShowReadyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              Your Workspace is Ready!
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <p className="text-sm text-muted-foreground">
              Everything is set up and ready to go. You can now access the dashboard and start managing your projects.
            </p>
            <Button
              onClick={handleGoToDashboard}
              className="w-full bg-blue-600 text-white hover:bg-blue-700"
            >
              Continue to Login
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Provisioning Failed
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button
              onClick={handleRetry}
              variant="destructive"
              className="w-full"
            >
              Try Again
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
